import { useCallback, useEffect, useRef, useState } from "react";
import { call } from "../../../common/jsonRpcRoutes";

/** Minimal venue summary returned by /fetchVenueList. */
export interface VenueSummary {
  _id: string;
  title: string;
  street?: string;
  city?: string;
  postcode?: string;
}

/**
 * Fetch venue summaries for a list of venue IDs.
 *
 * Skips the fetch when only one venue ID is provided (the caller already has
 * venue details via /createBooking's venueConfig response).
 */
export function useVenueList(
  venueIds: string[],
  source: string,
): {
  venues: VenueSummary[];
  loading: boolean;
  error: string | null;
  retry: () => void;
} {
  const [venues, setVenues] = useState<VenueSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const fetchVenues = useCallback(async () => {
    if (venueIds.length <= 1) return;

    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    try {
      const result = await call("/fetchVenueList", { venue_ids: venueIds, source });
      if (!cancelledRef.current) {
        setVenues(result);
      }
    } catch (err) {
      if (!cancelledRef.current) {
        setError(err instanceof Error ? err.message : "Failed to load venues");
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [venueIds, source]);

  useEffect(() => {
    void fetchVenues();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchVenues]);

  const retry = useCallback(() => {
    void fetchVenues();
  }, [fetchVenues]);

  return { venues, loading, error, retry };
}
